import React, { useMemo, useCallback } from 'react'
import styled from 'styled-components'
import clsx from 'clsx'
import { rem } from 'polished'
import Clickable from 'components/Common/Clickable'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import SolidStarCircleIcon from 'LuxKit/Icons/solid/SolidStarCircleIcon'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import { displayPricingAs, getCalendarDisplayTotalPrice } from 'lib/hotels/bedbankUtils'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import Group from 'components/utils/Group'
import { FormattedMessage, useIntl } from 'react-intl'

const CellWrapper = styled(Clickable)`
  display: flex;
  flex-direction: column;
  padding: ${rem(4)} ${rem(8)};
  transition: background-color 0.2s;
  min-height: ${rem(72)};

  box-shadow: 0 -1px 0 0 ${props => props.theme.palette.neutral.default.five},
  1px 0 0 0 ${props => props.theme.palette.neutral.default.five},
  1px 0 0 0 ${props => props.theme.palette.neutral.default.five} inset,
  0 -1px 0 0 ${props => props.theme.palette.neutral.default.five} inset;

  &:not(.check-in):not(.check-out):not(:disabled) {
    ${mediaHoverable} {
      &:hover {
        background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
      }
    }
    &:focus {
      background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
    }
  }

  &:disabled {
    background-color: ${props => props.theme.palette.neutral.default.seven};
  }

  &.check-in, &.check-out {
    background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
  }

  &.between-day {
    background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
  }
`

const Main = styled.div`
  opacity: 1;
  pointer-events: auto;
  transition: opacity 0.2s;
  display: flex;
  flex-direction: column;

  &.hidden {
    opacity: 0;
    pointer-events: none;
  }
`

interface Props {
  isCheckIn?: boolean
  isCheckOut?: boolean
  duration: number
  day: number
  hasPromo: boolean
  isCorporate: boolean
  price?: number
  propertyFees?: number
  flightPrice?: number
  style?: React.CSSProperties
  className?: string
  isBetweenDay?: boolean
  flights: boolean
  onClick: () => void
  adultsNumber: number
  roomsNumber: number
  displayPricingAsPerPerson: boolean
}

function CalendarDayCell(props: Props) {
  const {
    duration,
    day,
    price,
    propertyFees,
    isCheckIn,
    isCheckOut,
    style,
    className,
    isBetweenDay,
    hasPromo,
    isCorporate,
    flights,
    flightPrice,
    onClick,
    adultsNumber,
    roomsNumber,
    displayPricingAsPerPerson,
  } = props
  const intl = useIntl()
  const colour = (isCheckIn || isCheckOut) ? 'neutral-one' : undefined

  const checkInLabel = intl.formatMessage({
    id: 'Mkj8Kjn3vT',
    defaultMessage: 'Check in',
    description: 'Label shown on bedbank calendar day cell when it is the check-in date',
  })
  const checkOutLabel = intl.formatMessage({
    id: 'M0NXCzLdqt',
    defaultMessage: 'Check out',
    description: 'Label shown on bedbank calendar day cell when it is the check-out date',
  })
  const selectToViewPricesLabel = intl.formatMessage({
    id: 'fQXhk33s5O',
    defaultMessage: 'Select to view prices',
    description: 'Prompt shown on bedbank calendar day cell when no promo pricing is available',
  })

  const nightsPlusFlightsFromLabel = <FormattedMessage
    id="1xRxo7UUzt"
    defaultMessage="{nights, plural, one {# night} other {# nights}} + flights from"
    description="Bedbank calendar day cell: nights plus flights from label"
    values={{ nights: duration }}
  />
  const nightsFromLabel = <FormattedMessage
    id="Ugj6dj58gF"
    defaultMessage="{nights, plural, one {# night} other {# nights}} from"
    description="Bedbank calendar day cell: nights from label"
    values={{ nights: duration }}
  />

  const display = useMemo(() => {
    const cellSelection = {
      isBetweenDay,
      isCheckOut,
      isCheckIn,
      isNone: !isBetweenDay && !isCheckIn && !isCheckOut,
      shouldHideMainInformation: isBetweenDay || isCheckOut,
    }

    return {
      cellSelection,
    }
  }, [isBetweenDay, isCheckIn, isCheckOut])

  const onCellClick = useCallback(() => {
    if (isCheckIn) { return }
    onClick?.()
  }, [onClick, isCheckIn])

  const hotelPrice = price ?? 0
  const hotelPropertyFees = propertyFees ?? 0

  const {
    total,
    saleUnit,
  } = getCalendarDisplayTotalPrice({
    price: hotelPrice + hotelPropertyFees,
    adultsNumber,
    roomsNumber,
    withFlights: !!flights,
    flightPrice: flightPrice ?? 0,
    nights: duration,
    displayPricingPer: displayPricingAs(displayPricingAsPerPerson),
  })

  return (
    <CellWrapper
      style={style}
      className={clsx(className, {
        'check-in': display.cellSelection.isCheckIn,
        'check-out': display.cellSelection.isCheckOut,
        'between-day': display.cellSelection.isBetweenDay,
      })}
      onClick={onCellClick}
    >
      <Group direction="horizontal" horizontalAlign="space-between">
        <Heading colour={colour} as="span" variant="heading6">{day}</Heading>
        <BodyText colour="neutral-one" as="span" variant="small" weight="bold">
          {display.cellSelection.isCheckIn && checkInLabel}
          {display.cellSelection.isCheckOut && checkOutLabel}
        </BodyText>
        <div>
          {isCorporate && !display.cellSelection.isBetweenDay && <SolidStarCircleIcon size="S" colour={!colour ? 'highlight-secondary' : undefined} />}
          {hasPromo && !display.cellSelection.isBetweenDay && <SolidFireIcon size="XS" colour={!colour ? 'urgency' : undefined} />}
        </div>
      </Group>

      <Main className={clsx({ hidden: display.cellSelection.shouldHideMainInformation })}>
        {hasPromo && !!total && <>
          <Caption colour={colour ?? 'neutral-three'} variant="medium">
            {flights && flightPrice
              ? nightsPlusFlightsFromLabel
              : nightsFromLabel}
          </Caption>
          <div>
            <BodyText as="span" variant="medium" weight="bold">
              <FormatCurrency value={total} />
              {displayPricingAsPerPerson && <Caption variant="medium" as="span">{saleUnit}</Caption>}
            </BodyText>
            {!displayPricingAsPerPerson && <Caption colour={colour} as="span" variant="medium"> {saleUnit}</Caption>}
            {flights && !flightPrice && <Caption colour={colour} as="span" variant="medium">
              {intl.formatMessage({
                id: 'mtAEFkXdbD',
                defaultMessage: '+ flights',
                description: 'Suffix indicating flights price is additional on bedbank calendar day cell',
              })}
            </Caption>}
          </div>
        </>}

        {(!hasPromo || !total) && !display.cellSelection.isCheckIn && <Caption variant="large" colour={colour ?? 'neutral-three'} underline>{selectToViewPricesLabel}</Caption>}
      </Main>
    </CellWrapper>
  )
}

export default React.memo(CalendarDayCell)
